'use client'

import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useBirhaus } from '@birhaus/provider'
import { useBirhausThemeV3 } from '@birhaus/themes'
import { Brain, AlertTriangle, CheckCircle, XCircle, Activity } from 'lucide-react'

// Local cn utility
const cn = (...inputs: (string | boolean | undefined | null)[]) => twMerge(clsx(inputs))

// V3 Cognitive Load Meter variants with glass morphism
const cognitiveLoadMeterV3Variants = cva(
  [
    'relative w-full transition-all duration-500 ease-smooth',
    'backdrop-blur-md border border-white/30 shadow-lg overflow-hidden'
  ],
  {
    variants: {
      variant: {
        card: [
          'bg-white/60 backdrop-saturate-150 border-white/40',
          'rounded-3xl p-8'
        ],
        inline: [
          'bg-white/40 backdrop-saturate-120 border-white/30',
          'rounded-2xl p-6'
        ],
        minimal: [
          'bg-transparent border-transparent shadow-none',
          'rounded-xl p-4'
        ],
        floating: [
          'bg-white/70 backdrop-saturate-180 border-white/50',
          'rounded-[2rem] p-10 shadow-2xl'
        ]
      },
      size: {
        small: 'max-w-sm',
        medium: 'max-w-md',
        large: 'max-w-lg',
        fullWidth: 'w-full'
      },
      loadLevel: {
        optimal: 'ring-2 ring-green-500/30',
        moderate: 'ring-2 ring-yellow-500/30', 
        high: 'ring-2 ring-orange-500/30',
        critical: 'ring-2 ring-red-500/30'
      }
    },
    defaultVariants: {
      variant: 'card',
      size: 'medium',
      loadLevel: 'optimal'
    }
  }
)

// Progress bar variants for load visualization
const progressBarV3Variants = cva(
  [
    'h-3 rounded-full transition-all duration-700 ease-smooth',
    'backdrop-blur-sm overflow-hidden relative'
  ],
  {
    variants: {
      loadLevel: {
        optimal: 'bg-green-500/20 border border-green-500/30',
        moderate: 'bg-yellow-500/20 border border-yellow-500/30',
        high: 'bg-orange-500/20 border border-orange-500/30',
        critical: 'bg-red-500/20 border border-red-500/30'
      }
    },
    defaultVariants: {
      loadLevel: 'optimal'
    }
  }
)

export type CognitiveLoadLevel = 'optimal' | 'moderate' | 'high' | 'critical'

export interface CognitiveLoadData {
  navigationItems: number
  formFields: number
  visibleElements: number
  activeModals: number
  notifications: number
  interactiveElements: number
}

export interface BirhausCognitiveLoadMeterV3Props extends 
  VariantProps<typeof cognitiveLoadMeterV3Variants> {
  
  // Load data
  loadData: CognitiveLoadData
  maxOptimalLoad?: number
  maxModerateLoad?: number
  maxHighLoad?: number
  
  // Display options
  showDetails?: boolean
  showRecommendations?: boolean
  showMillersLawCompliance?: boolean
  animated?: boolean
  
  // V3 Glass styling
  enableGlass?: boolean
  reducedMotion?: boolean
  
  // Customization
  title?: string
  titleEs?: string
  titleEn?: string
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // Callbacks
  onLoadChange?: (level: CognitiveLoadLevel, score: number) => void
  onRecommendationClick?: (recommendation: string) => void
  
  // Theme
  themeName?: 'financial' | 'healthcare' | 'education'
  
  // Container props
  className?: string
  contentClassName?: string
}

/**
 * BirhausCognitiveLoadMeterV3 - Glass morphism cognitive load meter for BIRHAUS v3.0
 * 
 * Features:
 * - Glass morphism effects with backdrop blur and saturation
 * - Real-time cognitive load calculation based on Miller's Law
 * - Visual load level indication with color-coded progress
 * - Spanish-first labels and recommendations
 * - Detailed breakdown of UI complexity factors
 * - BIRHAUS compliance recommendations
 * - Generous spacing and radical border radius
 * - Performance-optimized animations
 * - Accessibility-compliant visual indicators
 */
export function BirhausCognitiveLoadMeterV3({
  loadData,
  variant = 'card',
  size = 'medium',
  maxOptimalLoad = 7,
  maxModerateLoad = 12,
  maxHighLoad = 18,
  showDetails = true,
  showRecommendations = true,
  showMillersLawCompliance = true,
  animated = true,
  enableGlass = true,
  reducedMotion = false,
  title,
  titleEs,
  titleEn,
  description,
  descriptionEs,
  descriptionEn,
  onLoadChange,
  onRecommendationClick,
  themeName = 'financial',
  className,
  contentClassName,
  ...props
}: BirhausCognitiveLoadMeterV3Props) {
  const { language } = useBirhaus()
  const theme = useBirhausThemeV3(themeName)
  
  // Calculate cognitive load score
  const cognitiveLoadScore = React.useMemo(() => {
    const {
      navigationItems,
      formFields,
      visibleElements,
      activeModals,
      notifications,
      interactiveElements
    } = loadData

    // Weighted scoring based on BIRHAUS principles
    return (
      navigationItems * 2 +          // Navigation has high cognitive weight
      formFields * 1.5 +             // Form fields are moderately complex
      Math.min(visibleElements, 20) * 0.5 + // Visible elements with cap
      activeModals * 3 +             // Modals disrupt focus significantly
      notifications * 2.5 +          // Notifications are highly disruptive
      interactiveElements * 1        // Interactive elements add base complexity
    )
  }, [loadData])

  // Determine load level
  const loadLevel: CognitiveLoadLevel = React.useMemo(() => {
    if (cognitiveLoadScore <= maxOptimalLoad) return 'optimal'
    if (cognitiveLoadScore <= maxModerateLoad) return 'moderate'  
    if (cognitiveLoadScore <= maxHighLoad) return 'high'
    return 'critical'
  }, [cognitiveLoadScore, maxOptimalLoad, maxModerateLoad, maxHighLoad])

  // Notify of load changes
  React.useEffect(() => {
    onLoadChange?.(loadLevel, cognitiveLoadScore)
  }, [loadLevel, cognitiveLoadScore, onLoadChange])

  // Get current texts based on language
  const currentTitle = React.useMemo(() => {
    if (title) return title
    return language === 'es' ? 
      (titleEs || 'Medidor de Carga Cognitiva') : 
      (titleEn || 'Cognitive Load Meter')
  }, [title, titleEs, titleEn, language])

  const currentDescription = React.useMemo(() => {
    if (description) return description
    return language === 'es' ? 
      (descriptionEs || 'Analiza la complejidad cognitiva de tu interfaz según los principios BIRHAUS') : 
      (descriptionEn || 'Analyzes your interface cognitive complexity following BIRHAUS principles')
  }, [description, descriptionEs, descriptionEn, language])

  // Generate recommendations
  const recommendations = React.useMemo(() => {
    const recs: string[] = []
    
    if (loadData.navigationItems > 4) {
      recs.push(language === 'es' ? 
        'Reduce elementos de navegación a máximo 4 (Regla 4-3-1)' :
        'Reduce navigation items to maximum 4 (4-3-1 Rule)')
    }
    
    if (loadData.formFields > 7) {
      recs.push(language === 'es' ? 
        'Divide formularios grandes (Ley de Miller: máximo 7±2 campos)' :
        'Break down large forms (Miller\'s Law: max 7±2 fields)')
    }
    
    if (loadData.activeModals > 1) {
      recs.push(language === 'es' ? 
        'Evita múltiples modales simultáneos' :
        'Avoid multiple simultaneous modals')
    }
    
    if (loadData.notifications > 3) {
      recs.push(language === 'es' ? 
        'Limita notificaciones activas para reducir distracciones' :
        'Limit active notifications to reduce distractions')
    }

    return recs
  }, [loadData, language])

  // Calculate progress percentage
  const progressPercentage = Math.min((cognitiveLoadScore / (maxHighLoad * 1.2)) * 100, 100)

  // Load level indicators
  const loadLevelConfig = {
    optimal: {
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-500/10',
      label: language === 'es' ? 'Óptimo' : 'Optimal'
    },
    moderate: {
      icon: Activity,
      color: 'text-yellow-600', 
      bgColor: 'bg-yellow-500/10',
      label: language === 'es' ? 'Moderado' : 'Moderate'
    },
    high: {
      icon: AlertTriangle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-500/10', 
      label: language === 'es' ? 'Alto' : 'High'
    },
    critical: {
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-500/10',
      label: language === 'es' ? 'Crítico' : 'Critical'
    }
  }

  const currentLevelConfig = loadLevelConfig[loadLevel]
  const LevelIcon = currentLevelConfig.icon

  return (
    <div
      className={cn(
        cognitiveLoadMeterV3Variants({
          variant,
          size,
          loadLevel
        }),
        className
      )}
      {...props}
    >
      {/* Glass gradient overlay */}
      {enableGlass && (
        <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-br from-white/20 via-transparent to-white/5 pointer-events-none" />
      )}

      {/* Content */}
      <div className={cn('relative z-10 space-y-6', contentClassName)}>
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className={cn(
            'p-3 rounded-2xl backdrop-blur-sm',
            currentLevelConfig.bgColor
          )}>
            <Brain size={24} className={currentLevelConfig.color} />
          </div>
          
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900">
              {currentTitle}
            </h3>
            {currentDescription && (
              <p className="text-sm text-gray-600 mt-1">
                {currentDescription}
              </p>
            )}
          </div>

          {/* Load level indicator */}
          <div className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-white/40 backdrop-blur-sm">
            <LevelIcon size={16} className={currentLevelConfig.color} />
            <span className={cn('text-sm font-medium', currentLevelConfig.color)}>
              {currentLevelConfig.label}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">
              {language === 'es' ? 'Puntuación de Carga' : 'Load Score'}
            </span>
            <span className="text-lg font-bold text-gray-900">
              {cognitiveLoadScore.toFixed(1)} / {maxHighLoad}
            </span>
          </div>
          
          <div className={cn(
            progressBarV3Variants({ loadLevel }),
            'relative overflow-hidden'
          )}>
            <div 
              className={cn(
                'h-full transition-all duration-700 ease-smooth',
                loadLevel === 'optimal' && 'bg-gradient-to-r from-green-500 to-green-600',
                loadLevel === 'moderate' && 'bg-gradient-to-r from-yellow-500 to-yellow-600',
                loadLevel === 'high' && 'bg-gradient-to-r from-orange-500 to-orange-600',
                loadLevel === 'critical' && 'bg-gradient-to-r from-red-500 to-red-600'
              )}
              style={{
                width: animated && !reducedMotion ? `${progressPercentage}%` : `${progressPercentage}%`,
                transitionDelay: animated ? '200ms' : '0ms'
              }}
            />
            
            {/* Glass shine effect */}
            {enableGlass && (
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none" />
            )}
          </div>
        </div>

        {/* Details breakdown */}
        {showDetails && (
          <div className="grid grid-cols-2 gap-4">
            {[
              { 
                key: 'navigationItems',
                labelEs: 'Navegación',
                labelEn: 'Navigation',
                value: loadData.navigationItems,
                maxRecommended: 4
              },
              { 
                key: 'formFields',
                labelEs: 'Campos de Form.',
                labelEn: 'Form Fields',
                value: loadData.formFields,
                maxRecommended: 7
              },
              { 
                key: 'activeModals',
                labelEs: 'Modales Activos',
                labelEn: 'Active Modals',
                value: loadData.activeModals,
                maxRecommended: 1
              },
              { 
                key: 'notifications',
                labelEs: 'Notificaciones',
                labelEn: 'Notifications',
                value: loadData.notifications,
                maxRecommended: 3
              }
            ].map((item) => (
              <div key={item.key} className="p-4 rounded-2xl bg-white/40 backdrop-blur-sm">
                <div className="text-sm text-gray-600 mb-1">
                  {language === 'es' ? item.labelEs : item.labelEn}
                </div>
                <div className={cn(
                  'text-lg font-semibold',
                  item.value > item.maxRecommended ? 'text-orange-600' : 'text-gray-900'
                )}>
                  {item.value} / {item.maxRecommended}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Miller's Law compliance */}
        {showMillersLawCompliance && (
          <div className="p-6 rounded-2xl bg-blue-50/60 backdrop-blur-sm border border-blue-200/40">
            <h4 className="font-semibold text-blue-900 mb-2">
              {language === 'es' ? 'Cumplimiento Ley de Miller' : 'Miller\'s Law Compliance'}
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-700">
                  {language === 'es' ? 'Navegación (4-3-1):' : 'Navigation (4-3-1):'}
                </span>
                <span className={cn(
                  'ml-2 font-medium',
                  loadData.navigationItems <= 4 ? 'text-green-600' : 'text-red-600'
                )}>
                  {loadData.navigationItems <= 4 ? '✓' : '✗'}
                </span>
              </div>
              <div>
                <span className="text-blue-700">
                  {language === 'es' ? 'Formularios (7±2):' : 'Forms (7±2):'}
                </span>
                <span className={cn(
                  'ml-2 font-medium',
                  loadData.formFields <= 7 ? 'text-green-600' : 'text-red-600'
                )}>
                  {loadData.formFields <= 7 ? '✓' : '✗'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Recommendations */}
        {showRecommendations && recommendations.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900">
              {language === 'es' ? 'Recomendaciones' : 'Recommendations'}
            </h4>
            <div className="space-y-2">
              {recommendations.map((rec, index) => (
                <button
                  key={index}
                  onClick={() => onRecommendationClick?.(rec)}
                  className={cn(
                    'w-full text-left p-4 rounded-2xl',
                    'bg-amber-50/60 backdrop-blur-sm border border-amber-200/40',
                    'hover:bg-amber-100/60 transition-colors',
                    'focus:outline-none focus:ring-2 focus:ring-amber-500/30'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-amber-800">{rec}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}